import type { FC } from "react";
import { Box, Container, Typography, Stack, Chip } from "@mui/material";
import { motion } from "framer-motion";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ShieldIcon from "@mui/icons-material/Shield";
import BoltIcon from "@mui/icons-material/Bolt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const services = [
  {
    icon: <SendIcon sx={{ fontSize: 30 }} />,
    subtitle: "Global Money Transfer",
    title: "International Remittance",
    description:
      "Transfer funds to 30+ countries with best-in-class exchange rates. Instant bank-to-bank transfers with zero hidden fees and real-time tracking at every step.",
    features: [
      "Real-time competitive exchange rates",
      "Multi-currency support (USD, EUR, GBP & more)",
      "Instant bank-to-bank transfers",
      "Full transaction tracking & SMS alerts",
    ],
    color: "#818CF8",
    gradient: "linear-gradient(135deg, rgba(99, 102, 241, 0.16) 0%, rgba(99, 102, 241, 0.04) 100%)",
    border: "rgba(129, 140, 248, 0.28)",
    hoverBorder: "#818CF8",
    hoverShadow: "0 24px 60px rgba(99, 102, 241, 0.22)",
  },
  {
    icon: <SwapHorizIcon sx={{ fontSize: 30 }} />,
    subtitle: "Institutional Grade",
    title: "OTC Trading Desk",
    description:
      "Execute large-volume fiat-to-crypto trades with deep liquidity. Personalized service for businesses and HNWIs — from ₹10 Lakhs to ₹100 Crores+ with same-day settlement.",
    features: [
      "Trades from ₹10 Lakhs to ₹100 Crores+",
      "Fiat ↔ Crypto seamless conversions",
      "Dedicated relationship manager",
      "Same-day settlement & competitive pricing",
    ],
    color: "#FCD34D",
    gradient: "linear-gradient(135deg, rgba(252, 211, 77, 0.12) 0%, rgba(249, 115, 22, 0.04) 100%)",
    border: "rgba(252, 211, 77, 0.25)",
    hoverBorder: "#FCD34D",
    hoverShadow: "0 24px 60px rgba(252, 211, 77, 0.16)",
  },
];

const pillars = [
  { icon: <AccountBalanceIcon />, title: "Bank-Grade Security", desc: "256-bit encryption & multi-factor auth on every transaction" },
  { icon: <BoltIcon />,          title: "Lightning Fast",       desc: "Process transfers in seconds powered by modern payment rails" },
  { icon: <ShieldIcon />,        title: "Fully Compliant",      desc: "RBI & FEMA compliant operations with transparent audit trails" },
  { icon: <SupportAgentIcon />,  title: "24/7 Support",         desc: "Dedicated relationship managers available around the clock" },
];

export const HomeServices: FC = () => (
  <Box
    component="section"
    id="services"
    sx={{
      background: "linear-gradient(180deg, #0C0A22 0%, #08091A 100%)",
      py: { xs: 10, md: 15 },
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Ambient glow */}
    <Box sx={{
      position: "absolute", top: "40%", left: "50%",
      transform: "translate(-50%, -50%)",
      width: 900, height: 500,
      background: "radial-gradient(ellipse, rgba(99, 102, 241, 0.12) 0%, transparent 70%)",
      filter: "blur(60px)", zIndex: 0, pointerEvents: "none",
    }} />
    <Box sx={{
      position: "absolute", bottom: 0, right: "10%",
      width: 400, height: 400, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)",
      filter: "blur(70px)", zIndex: 0, pointerEvents: "none",
    }} />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 6, md: 9 } }}>
        <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <Chip label="Our Services" sx={{
            mb: 2.5, bgcolor: "rgba(99, 102, 241, 0.18)", color: "#A5B4FC",
            border: "1px solid rgba(129, 140, 248, 0.38)", fontWeight: 700, letterSpacing: "0.1em",
          }} />
          <Typography variant="h2" sx={{ color: "#fff", fontWeight: 800, mb: 2, fontSize: { xs: "2rem", md: "3rem" }, lineHeight: 1.15 }}>
            Everything you need for{" "}
            <Box component="span" sx={{
              background: "linear-gradient(135deg, #818CF8, #22D3EE)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              global finance
            </Box>
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "1.05rem", maxWidth: 560, mx: "auto", lineHeight: 1.75 }}>
            From personal remittances to institutional OTC trades — end-to-end financial solutions with unmatched speed and security.
          </Typography>
        </motion.div>
      </Box>

      {/* Service cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: { xs: 6, md: 8 } }}>
        {services.map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: i * 0.15 }}>
            <Box sx={{
              background: s.gradient, border: `1px solid ${s.border}`,
              borderRadius: 4, p: { xs: 3.5, md: 4.5 }, height: "100%",
              cursor: "pointer", transition: "all 0.35s ease",
              "&:hover": { transform: "translateY(-6px)", borderColor: s.hoverBorder, boxShadow: s.hoverShadow },
            }}>
              <Box sx={{ display: "inline-flex", p: 1.5, borderRadius: 2, mb: 3, background: `${s.color}22`, color: s.color }}>
                {s.icon}
              </Box>
              <Typography sx={{ color: s.color, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", mb: 0.75 }}>
                {s.subtitle}
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 2, fontSize: { xs: "1.5rem", md: "1.75rem" } }}>
                {s.title}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 3.5, lineHeight: 1.75, fontSize: "0.93rem" }}>
                {s.description}
              </Typography>
              <Stack spacing={1.5}>
                {s.features.map((f) => (
                  <Stack key={f} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircleOutlineIcon sx={{ fontSize: 17, color: s.color, flexShrink: 0 }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.875rem" }}>{f}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Pillars */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2.5 }}>
        {pillars.map((p, i) => (
          <motion.div key={p.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}>
            <Box sx={{
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(129, 140, 248, 0.14)",
              borderRadius: 3, p: 3, textAlign: "center",
              transition: "all 0.3s ease",
              "&:hover": { borderColor: "rgba(129, 140, 248, 0.5)", background: "rgba(99, 102, 241, 0.1)", transform: "translateY(-3px)" },
            }}>
              <Box sx={{ color: "#818CF8", mb: 1.5, "& svg": { fontSize: 28 } }}>{p.icon}</Box>
              <Typography sx={{ color: "#fff", fontWeight: 600, mb: 0.75, fontSize: "0.92rem" }}>{p.title}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.48)", fontSize: "0.78rem", lineHeight: 1.55 }}>{p.desc}</Typography>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  </Box>
);
